import { MouseEventHandler, useEffect, useState } from "react"
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useDispatch, useSelector } from "react-redux"
import DeleteConfirmPurchase from "../components/DeleteConfirmPurchase"
import Modal from "../components/Modal"
import { FormInput } from "../components/custom/components"
import { FormType, FormTypeList } from "../config"
import { getDate, LocalFormatDate } from "../config/functions"
import { AccountHolderType } from "../redux/actions/accountHolder"
import { AuthUserType, RoleType } from "../redux/actions/authUserActions"
import { fetchPurchaseByDateAction, PurchaseType } from "../redux/actions/purchaseActions"
import { AppDispatch, StoreState } from "../redux/store"


interface TableBody {
    sno: number
    invoiceDate: string
    invoiceNo: string
    purchaseEntryNo: string
    supplier: string
    finalAmount: string
    remainingBill: string
    showEditButton?: boolean
    onEdit: MouseEventHandler<HTMLButtonElement>
    onDelete: MouseEventHandler<HTMLButtonElement>
    rolesAccessObj: RoleType | undefined
}

type HandleEdit = (value: PurchaseType) => void
type HandleDelete = (value: PurchaseType) => void

interface Props {
    setFormType: React.Dispatch<React.SetStateAction<FormType>>
    setId: React.Dispatch<React.SetStateAction<number | undefined>>
    onclose: () => void
    From: string
    /** When the view modal opens, refetch purchases for the selected filters */
    viewOpen?: boolean
}

const toApiDate = (date: Date | null): string => getDate(date ?? new Date())

const Purchase = (props: Props) => {
    const dispatch = useDispatch<AppDispatch>()
    const purchaseList = useSelector<StoreState, PurchaseType[]>(state => state.purchase)
    const AuthUser = useSelector<StoreState, AuthUserType | null>(state => state.authUser);
    const AccountLists = useSelector<StoreState, AccountHolderType[]>(state => state.account)

    const [showDeleteForm, setShowDeleteForm] = useState<boolean>(false)
    const [editData, setEditData] = useState<PurchaseType>()

    const [searchDate, setSearchDate] = useState<Date | null>(new Date());
    const [searchSupplier, setSearchSupplier] = useState<string>('');
    const [searchInvoiceNo, setSearchInvoiceNo] = useState<string>('');

    const url = window.location.pathname
    const rolesAccessObj = AuthUser?.roles?.find(f => f.pageUrl === url)

    const loadPurchases = (
        date: Date | null,
        invoiceNo = searchInvoiceNo,
        supplier = searchSupplier,
    ) => {
        const apiDate = toApiDate(date)
        dispatch(fetchPurchaseByDateAction(apiDate, apiDate, {
            invoiceNo,
            supplier,
        }))
    }

    useEffect(() => {
        if (props.viewOpen === false) return
        const timer = window.setTimeout(() => {
            loadPurchases(searchDate, searchInvoiceNo, searchSupplier)
        }, searchInvoiceNo || searchSupplier ? 300 : 0)
        return () => window.clearTimeout(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.viewOpen, searchDate, searchInvoiceNo, searchSupplier, dispatch])

    const handleEdit: HandleEdit = (value) => {
        props.setFormType(FormTypeList.EDIT)
        props.setId(value.id)
        setEditData(value)
        props.onclose()
    }

    const handleDelete: HandleDelete = (value) => {
        props.setFormType(FormTypeList.DELETE)
        setEditData(value)
        setShowDeleteForm(true)
    }

    const handleDeleteFormClose = () => {
        setShowDeleteForm(false)
        loadPurchases(searchDate)
    }

    const handleDateFilter = (date: Date | null) => {
        setSearchDate(date ?? new Date());
    };

    const handleSupplierFilter = (value: string) => {
        setSearchSupplier(value);
    };

    const handleInvoiceNoFilter = (value: string) => {
        setSearchInvoiceNo(value);
    };

    const filteredPurchaseList = purchaseList.filter((purchase) =>
        props.From === 'OpeningStock' ? purchase.remarks === 'OpeningStock' : true
    );

    return <>
        <div className="row">
            <div className="col-lg-12">
                <div className="hstack gap-4 mb-3">
                    <div>
                        <label htmlFor="searchDate">Date :</label>
                        <DatePicker
                            id="searchDate"
                            selected={searchDate}
                            onChange={(date) => handleDateFilter(date)}
                            dateFormat="dd-MM-yyyy"
                        />
                    </div>
                    <FormInput
                        name='searchInvoiceNo'
                        label='Invoice No :'
                        value={searchInvoiceNo}
                        onChange={(e) => handleInvoiceNoFilter(e.target.value)}
                    />
                    <FormInput
                        name='searchSupplier'
                        label='Supplier :'
                        value={searchSupplier}
                        onChange={(e) => handleSupplierFilter(e.target.value)}
                    />
                </div>
            </div>
        </div>

        <Modal
            headerText={'Delete Purchase'}
            visible={showDeleteForm}
            onClose={handleDeleteFormClose}
            centered
            size='sm'
        >
            <DeleteConfirmPurchase
                editData={editData}
                onClose={handleDeleteFormClose}
            />
        </Modal>
        <div className="row">
            <div className="col-lg-12">
                <div className="card shadow-lg">
                    <div className='table-wrapper' style={{ zIndex: "0" }}>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th className='text-truncate align-middle'>S.No</th>
                                    <th className='text-truncate align-middle'>Purchase Entry No</th>
                                    <th className='text-truncate align-middle'>Invoice Date</th>
                                    <th className='text-truncate align-middle'>Invoice No</th>
                                    <th className='text-truncate align-middle'>Supplier</th>
                                    <th className='text-truncate align-middle'>Grand Total</th>
                                    <th className='text-truncate align-middle'>Status</th>
                                    <th className='text-truncate align-middle'>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPurchaseList.map((tr, i) => {
                                    let sobj = AccountLists?.find(s => s?.accountNumber === tr?.supplierId)
                                    let sname = sobj?.accountName ? sobj?.accountName : ''
                                    return <UserBody
                                        sno={i + 1}
                                        invoiceNo={tr.invoiceNumber?.toString()}
                                        invoiceDate={tr.invoiceDate?.toString()}
                                        purchaseEntryNo={tr.purchaseEntryNumber?.toString()}
                                        finalAmount={tr.grandTotal ? tr.grandTotal?.toString() : '0'}
                                        remainingBill={tr.remainingRecordExist ? 'NOT COMPLETED' : 'COMPLETED'}
                                        supplier={sname}
                                        showEditButton={!!tr?.remainingRecordExist}
                                        onEdit={() => handleEdit(tr)}
                                        onDelete={() => handleDelete(tr)}
                                        rolesAccessObj={rolesAccessObj}
                                        key={i}

                                    />
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </>
}

const UserBody = ({ sno, invoiceDate, finalAmount, invoiceNo, purchaseEntryNo, supplier, remainingBill, onEdit, onDelete, rolesAccessObj }: TableBody) => {

    return <tr key={sno}>
        <td className='align-middle'>{sno}</td>
        <td className='text-capitalize fw-bold align-middle'>{purchaseEntryNo}</td>
        <td className='text-capitalize fw-bold align-middle'>{LocalFormatDate(invoiceDate)}</td>
        <td className='text-capitalize fw-bold align-middle'>{invoiceNo}</td>
        <td className='text-capitalize fw-bold align-middle'>{supplier}</td>
        <td className='text-capitalize fw-bold align-middle'>{finalAmount}</td>
        <td><span className={`align-middle fs-5 badge ${remainingBill === 'NOT COMPLETED' ? 'bg-danger blink' : 'bg-success'}`}>
            {remainingBill}
        </span></td>
        <td>
            {/* <div className=' hstack gap-1  justify-content-center'>
                <button className='btn btn-sm fw-bold btn-info ' onClick={onEdit}><i className="fe-edit noti-icon"></i></button>
                {(AuthUser?.role === 'SUPER' && <button className='btn btn-sm fw-bold btn-danger ' onClick={onDelete}><i className="fe-trash-2 noti-icon"></i></button>)}
            </div> */}
            <div className="hstack gap-1">
                {(rolesAccessObj?.edit || rolesAccessObj?.delete) &&
                    <div className="hstack gap-1">
                        {rolesAccessObj?.edit && <button className="btn btn-sm fw-bold btn-info p-1" onClick={onEdit}>
                            <i className="fe-edit noti-icon"></i>
                        </button>}
                        {rolesAccessObj?.delete && <button className="btn btn-sm fw-bold btn-danger p-1" onClick={onDelete}>
                            <i className="fe-trash-2 noti-icon"></i>
                        </button>}
                    </div>
                }
            </div>

        </td>
    </tr>
}
export default Purchase
